/**
 * Bridge between the theme's own "Уведоми ме при наличност" buttons and the
 * Kbite / CartBite back-in-stock app.
 *
 * The app's inline placement is `document.querySelector('[name=add]').parentNode` —
 * one button per page, appended after the sold-out button. That puts it beside the
 * quantity picker on the PDP and does nothing at all on a grid of product cards.
 * So the theme renders the triggers (snippets/notify-when-available.liquid) and this
 * file wires them up:
 *
 *   • window.stockWidgetButtonParent → an off-screen sink, so the app's own copy of
 *     the button is created (which is what boots its modal) but never seen.
 *   • a click calls growConfig.stockWidgetClicked({ target: button }); the app reads
 *     data-product-id / data-variant-id / data-product-handle straight off the target,
 *     so any element carrying those three can open the subscribe modal.
 *   • growConfig.addPopUp() builds the modal iframe on the pages the app skips (it only
 *     renders one under /products/), which is what makes the card buttons work. It is
 *     built on the first hover / focus / touch of a trigger, not on page load: the iframe
 *     pulls Vue, vue-tel-input and vue-select (+ their CSS) from jsDelivr, ~200 KB that
 *     every home and collection page downloaded for a modal nobody had asked for
 *     (PageSpeed "unused CSS" 72 KB, unused JS).
 *
 * If the app never loads, <html data-pm-notify-unavailable> flips the CSS back to the
 * native sold-out button rather than leaving a trigger that does nothing.
 */
(function () {
  const SINK_ID = 'pm-notify-sink';
  const UNAVAILABLE_ATTR = 'data-pm-notify-unavailable';
  const APP_TIMEOUT = 12000;
  const MODAL_TIMEOUT = 10000; // the modal may only start booting on the click itself (touch)
  const PREFILL_TIMEOUT = 4000; // the modal renders its form after the iframe reports ready
  const POLL_INTERVAL = 200;
  const EMAIL_SELECTOR = 'input[name="email"]';

  function app() {
    const cfg = window.growConfig;
    return cfg && cfg.stockWidget && typeof cfg.stockWidgetClicked === 'function' ? cfg : null;
  }

  /** Poll until `test()` is truthy or we run out of patience. Resolves to its result. */
  function waitFor(test, timeout) {
    return new Promise((resolve) => {
      const first = test();
      if (first) return resolve(first);
      let waited = 0;
      const timer = window.setInterval(() => {
        const value = test();
        waited += POLL_INTERVAL;
        if (value || waited >= timeout) {
          window.clearInterval(timer);
          resolve(value);
        }
      }, POLL_INTERVAL);
    });
  }

  function installSink() {
    let sink = document.getElementById(SINK_ID);
    if (!sink) {
      sink = document.createElement('div');
      sink.id = SINK_ID;
      sink.setAttribute('aria-hidden', 'true');
      document.body.appendChild(sink);
    }
    window.stockWidgetButtonParent = sink;
  }

  function ensureModal(cfg) {
    try {
      cfg.addPopUp(cfg.stockWidget.popupSelector, cfg.stockWidget.modalHtml);
    } catch (e) {
      /* on product pages the app has already built it */
    }
  }

  /**
   * The modal is a same-origin iframe written from this page, so a signed-in
   * customer's address can be put in the email field for them. The field is bound
   * to the modal's own Vue instance, hence the native setter plus input/change —
   * assigning `.value` on its own leaves the model empty and the form submits
   * without an address.
   */
  function prefillEmail(cfg) {
    const email = window.PM_CUSTOMER_EMAIL;
    if (!email) return Promise.resolve();
    const frame = document.getElementById(cfg.stockWidget.popupSelector);
    let doc = null;
    try {
      doc = frame && frame.contentDocument;
    } catch (e) {
      return Promise.resolve(); // cross-origin one day: leave it to the app
    }
    if (!doc) return Promise.resolve();
    return waitFor(() => doc.querySelector(EMAIL_SELECTOR), PREFILL_TIMEOUT).then((input) => {
      if (!input || input.value) return; // never overwrite what the visitor typed
      const view = doc.defaultView;
      const setter = Object.getOwnPropertyDescriptor(view.HTMLInputElement.prototype, 'value').set;
      setter.call(input, email);
      input.dispatchEvent(new view.Event('input', { bubbles: true }));
      input.dispatchEvent(new view.Event('change', { bubbles: true }));
    });
  }

  /** The modal iframe registers itself here once it has booted its Vue instance. */
  function modalReady(cfg) {
    return !!(window.growCampaignPopUps && window.growCampaignPopUps[cfg.stockWidget.popupSelector]);
  }

  /**
   * A variant change re-renders only the submit button (product-info.js →
   * toggleAddButton), so the buy box's --notify state and the trigger's variant id
   * have to be kept in step here.
   */
  function syncBuyBox(sectionId, variant) {
    const root = sectionId ? document.querySelector('product-info[data-section="' + sectionId + '"]') : null;
    const soldOut = !!variant && variant.available === false;
    (root || document).querySelectorAll('.product-form__buttons').forEach((box) => {
      const btn = box.querySelector('[data-pm-notify]');
      if (!btn) return;
      if (variant && variant.id) btn.dataset.variantId = variant.id;
      box.classList.toggle('product-form__buttons--notify', soldOut);
    });
  }

  async function openNotify(btn) {
    btn.classList.add('pm-notify--busy');
    try {
      const cfg = await waitFor(app, APP_TIMEOUT);
      if (!cfg) {
        document.documentElement.setAttribute(UNAVAILABLE_ATTR, '');
        return;
      }
      ensureModal(cfg);
      await waitFor(() => modalReady(cfg), MODAL_TIMEOUT);
      await cfg.stockWidgetClicked({ target: btn });
      await prefillEmail(cfg);
    } catch (e) {
      /* the app owns the modal; a failure there is its own to report */
    } finally {
      btn.classList.remove('pm-notify--busy');
    }
  }

  function init() {
    installSink();

    // Delegated: cards are re-rendered by facets, the buy box by variant changes.
    document.addEventListener('click', (event) => {
      const btn = event.target.closest && event.target.closest('[data-pm-notify]');
      if (!btn) return;
      event.preventDefault();
      openNotify(btn);
    });

    const pubsub = window.FoxTheme && window.FoxTheme.pubsub;
    if (pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.variantChange) {
      pubsub.subscribe(pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
        const data = (event && event.data) || {};
        syncBuyBox(data.sectionId, data.variant);
      });
    }

    waitFor(app, APP_TIMEOUT).then((cfg) => {
      if (!cfg) {
        document.documentElement.setAttribute(UNAVAILABLE_ATTR, '');
        return;
      }
      // Warm the modal on intent; openNotify() still builds it if the click comes first.
      const INTENT = ['pointerover', 'focusin', 'touchstart'];
      const warm = (event) => {
        if (!event.target.closest || !event.target.closest('[data-pm-notify]')) return;
        INTENT.forEach((type) => document.removeEventListener(type, warm, true));
        ensureModal(cfg);
      };
      INTENT.forEach((type) => document.addEventListener(type, warm, { capture: true, passive: true }));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
